import { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';
import client from '../api/client';

const ChatContext = createContext(null);

const SOCKET_URL =
  process.env.REACT_APP_SOCKET_URL ||
  (process.env.REACT_APP_API_URL || '').replace(/\/api\/v1\/?$/, '') ||
  '';

export function ChatProvider({ user, children }) {
  const [contacts, setContacts]   = useState([]);
  const [messages, setMessages]   = useState({});   // { [contactUserId]: Message[] }
  const [activeContact, setActiveContact] = useState(null);
  const [typing, setTyping]       = useState({});   // { [userId]: bool }
  const [connected, setConnected] = useState(false);
  const [sendError, setSendError] = useState(null);
  const socketRef    = useRef(null);
  const typingTimers = useRef({});

  // ── Socket — always live while user is logged in ──────────────────────────
  useEffect(() => {
    if (!user || !['student', 'teacher'].includes(user.role)) return;
    const token = localStorage.getItem('edupulse_token');
    if (!token) return;

    const socket = io(SOCKET_URL, {
      auth: { token },
      transports: ['polling', 'websocket'],
      upgrade: true,
      reconnection: true,
      reconnectionDelay: 2000,
      reconnectionAttempts: 10,
    });
    socketRef.current = socket;

    socket.on('connect',    () => setConnected(true));
    socket.on('disconnect', () => setConnected(false));

    socket.on('chat:message', (msg) => {
      const myId = (user._id || user.id)?.toString();
      const otherId = msg.senderId === myId ? msg.receiverId : msg.senderId;

      setMessages(prev => ({
        ...prev,
        [otherId]: [...(prev[otherId] || []), msg],
      }));

      // Bump unread badge if the message is from someone else
      if (msg.senderId !== myId) {
        setContacts(prev =>
          prev.map(c =>
            c.userId === msg.senderId
              ? { ...c, unread: (c.unread || 0) + 1 }
              : c
          )
        );
      }
    });

    socket.on('chat:typing', ({ fromUserId }) => {
      setTyping(prev => ({ ...prev, [fromUserId]: true }));
      clearTimeout(typingTimers.current[fromUserId]);
      typingTimers.current[fromUserId] = setTimeout(
        () => setTyping(prev => ({ ...prev, [fromUserId]: false })),
        2500
      );
    });

    socket.on('chat:error', (err) => {
      console.error('[chat]', err.message);
      setSendError(err.message || 'Failed to send message');
    });

    return () => {
      socket.disconnect();
      socketRef.current = null;
      setConnected(false);
    };
  }, [user]);

  // ── REST helpers ──────────────────────────────────────────────────────────
  const loadContacts = useCallback(async () => {
    try {
      const { data } = await client.get('/chat/contacts');
      setContacts(data.contacts || []);
    } catch (err) {
      console.error('[chat] loadContacts:', err.message);
    }
  }, []);

  const loadHistory = useCallback(async (contactUserId) => {
    try {
      const { data } = await client.get(`/chat/history/${contactUserId}`);
      setMessages(prev => ({ ...prev, [contactUserId]: data.messages || [] }));
      setContacts(prev => prev.map(c => c.userId === contactUserId ? { ...c, unread: 0 } : c));
      client.patch(`/chat/read/${contactUserId}`).catch(() => {});
    } catch (err) {
      console.error('[chat] loadHistory:', err.message);
    }
  }, []);

  // ── Actions ───────────────────────────────────────────────────────────────
  const sendMessage = useCallback((toUserId, message) => {
    if (!socketRef.current?.connected || !message.trim()) {
      setSendError(!socketRef.current?.connected ? 'Not connected — check your internet' : null);
      return false;
    }
    setSendError(null);
    socketRef.current.emit('chat:send', { toUserId, message: message.trim() });
    return true;
  }, []);

  const sendTyping = useCallback((toUserId) => {
    socketRef.current?.emit('chat:typing', { toUserId });
  }, []);

  const openContact = useCallback(async (contact) => {
    setActiveContact(contact);
    // Load history if not already loaded
    if (!messages[contact.userId]) {
      await loadHistory(contact.userId);
    } else {
      // Already loaded — just clear badge and mark read on server
      setContacts(prev => prev.map(c => c.userId === contact.userId ? { ...c, unread: 0 } : c));
      client.patch(`/chat/read/${contact.userId}`).catch(() => {});
    }
  }, [messages, loadHistory]);

  // Load contacts once on mount (or when user changes)
  useEffect(() => {
    if (user && ['student', 'teacher'].includes(user.role)) loadContacts();
  }, [user, loadContacts]);

  const totalUnread = contacts.reduce((sum, c) => sum + (c.unread || 0), 0);

  return (
    <ChatContext.Provider value={{
      connected, contacts, messages, activeContact, typing,
      sendError, setSendError,
      openContact, sendMessage, sendTyping, loadContacts,
      totalUnread,
    }}>
      {children}
    </ChatContext.Provider>
  );
}

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used inside ChatProvider');
  return ctx;
}
